import { useMemo } from "react";
import { ValidationError } from "yup";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { formatLimitAmount } from "@/shared/utils/amountLimits";
import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";
import { pixKeyValid, resolvePixKeyType } from "@/shared/utils/pixKey";

import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  railForQuote,
  resolveColRail,
  resolveRuntimeCorridor,
} from "../constants/dispersions.constants";
import { BrebKeyType } from "../services/paymentsPayout.service";
import { ValidationSchemaService } from "../services/validationSchemas.service";
import { resolveBrebAuthorizationBlock, validateColombiaDestination } from "../utils/colombiaDestination";
import { formatDecimalAmount2, getCurrencySymbol, parseAmountInputDecimal } from "../utils/currencyFormatter";
import { payoutPayerTypeFor, resolvePayoutLimitViolation } from "../utils/payoutLimitCheck";
import { type PayoutQuoteBlock, resolvePayoutQuoteBlock } from "../utils/quoteReadiness";
import { brebDirectoryOutcome, useBrebKeyResolution } from "./useBrebKeyResolution";
import { useDispersions } from "./useDispersions";

/** Why the quote cannot back the payout, said to the operator. Every key already existed but one. */
const QUOTE_BLOCK_MESSAGE = {
  pricing: "error_quote_pricing",
  missing: "error_quote_required",
  wrong_service: "error_quote_invalid",
  expired: "error_quote_expired",
  corridor_mismatch: "error_quote_invalid",
} as const satisfies Record<NonNullable<PayoutQuoteBlock>, string>;

// PIX key validity by type (Brazil / new backend) — same shared helper the Step 2 panel and the
// client-account form use.
function isPixValid(type: string, key: string): boolean {
  const resolved = resolvePixKeyType(type);
  return resolved ? pixKeyValid(resolved, key) : false;
}

/**
 * Wizard validation for the current step, extracted verbatim from `DispersionsListPage` (the page is a pure
 * orchestrator now). Rules per step: - Step 1: Yup schema (origin/destination/amount) + PHP per-transaction
 * limits + funds check — against the runtime ledger wallet for BRA/MEX (no overdraft, frozen blocks) or the
 * PHP balance elsewhere. - Step 2: client + account selection (only when one is chosen), unverified-client
 * KYC gate, and the corridor destination rules (PIX key / 18-digit CLABE / beneficiary / description). - Step
 * 3: no fields left to validate, but the QUOTE about to be redeemed must still be apt, and on Bre-B the
 * RECIPIENT must still be confirmed. The quote check used to live only in the submit, which runs after the
 * click — so the button stayed enabled over a quote the server was about to refuse and the operator got an
 * error screen instead of a disabled button. The recipient check did not exist at all: the Colombia rules run
 * only on step 2, so a directory answer that arrived after Continue was pressed never reached the Authorize
 * button. `isFormValid` gates the Continue/Authorize button, and therefore `handleNext` too: a money guard
 * must not depend on the DOM `disabled` attribute alone.
 */
export function useDispersionFormErrors(): {
  formErrors: Record<string, string>;
  isFormValid: boolean;
  /** Why step 3 is blocked by the PRICE, ready to render. Null when nothing blocks it. */
  quoteBlockReason: string | null;
  /** Why step 3 is blocked by WHO RECEIVES. Null when nothing blocks it. */
  recipientBlockReason: string | null;
} {
  const {
    quoteData,
    quoteLoading,
    quoteExpired,
    currentStep,
    formData,
    getRuntimeWalletForCurrency,
    getRuntimeWalletError,
    verifiedClientIds,
    clientKycLoading,
    clientKycError,
  } = useDispersions();
  const { user, isInternalUser } = useAuth();
  const { t, activeLanguage } = useTranslations("dispersions");
  const corridors = useAvailableCorridors();

  // The Bre-B directory now gates the step, so the validation needs the same answer the field shows. It
  // costs no extra request: `useBrebKeyResolution` shares one cached result across every caller.
  const brebKeyTypeRaw = String(formData.recipientFields?.breb_key_type ?? "").toUpperCase();
  const brebResolution = useBrebKeyResolution(
    (Object.values(BrebKeyType) as string[]).includes(brebKeyTypeRaw)
      ? (brebKeyTypeRaw as BrebKeyType)
      : BrebKeyType.PHONE,
    String(formData.recipientFields?.breb_key_value ?? ""),
    resolveRuntimeCorridor(formData.destination) === "COL" &&
      resolveColRail(formData.recipientFields) !== "TRANSFIYA",
  );

  const validationSchema = useMemo(() => {
    if (currentStep === 1) {
      return ValidationSchemaService.getStep1ValidationSchema({
        originRequired: t("error_origin_required"),
        destinationRequired: t("error_destination_required"),
        amountRequired: t("error_amount_required"),
        amountPositive: t("error_amount_positive"),
      });
    }
    return undefined;
  }, [currentStep, t]);

  const formErrors = useMemo(() => {
    const errors: Record<string, string> = {};

    if (currentStep === 2) {
      // Canonical corridor detection — single source of the spelling tolerance (constants).
      const runtimeCorridor = resolveRuntimeCorridor(formData.destination);
      const isBrazil = runtimeCorridor === "BRA";
      const isMexico = runtimeCorridor === "MEX";
      const isArgentina = runtimeCorridor === "ARG";
      const isColombia = runtimeCorridor === "COL";
      // Manual mode — the default: the recipient is typed in full, so no client/account selection is
      // required. The corridor guard is what keeps the legacy (PHP) corridors on the client path: they
      // render no mode switch, so their `recipientMode` is whatever the default happens to be and must not
      // be allowed to waive a selection their panel does ask for.
      const isOneOffRecipient = formData.recipientMode === "manual" && runtimeCorridor !== null;
      if (!isOneOffRecipient) {
        if (!formData.selectedClientId) {
          errors.selectedClientId = t("error_client_required");
        } else if (verifiedClientIds === null) {
          // KYC list unknown. This gate is the ONLY enforcement (the backend does not re-check at
          // dispersal), so an errored or still-loading fetch fails CLOSED — the recipient panels surface
          // the retry alert. INTERNAL users never fetch (no error, no loading) and keep fail-open.
          if (clientKycError !== null || clientKycLoading) {
            errors.selectedClientId = t("client_kyc_unavailable_note");
          }
        } else if (!verifiedClientIds.includes(formData.selectedClientId)) {
          // Business rule: an unverified client cannot receive a dispersal — even with registered accounts.
          errors.selectedClientId = t("client_kyc_blocked_note");
        }
        if (!formData.selectedClientAccountId) {
          errors.selectedClientAccountId = t("error_client_account_required");
        }
      }
      if (isBrazil) {
        const pixType = String(formData.recipientFields?.pix_type ?? "");
        const pixKey = String(formData.recipientFields?.pix_key ?? "");
        const beneficiary = String(formData.recipientFields?.beneficiary ?? "");
        if (!isPixValid(pixType, pixKey)) {
          errors.pix_key = t("error_pix_key_invalid");
        }
        if (beneficiary.trim().length < 2) {
          errors.beneficiary = t("error_recipient_name_required");
        }
      }
      // México (new backend): the SPEI destination requires an 18-digit CLABE
      // and a beneficiary name (SpeiDestinationDto).
      if (isMexico) {
        const clabe = String(formData.recipientFields?.clabe ?? "").replace(/\D/g, "");
        const beneficiary = String(formData.recipientFields?.beneficiary ?? "");
        if (clabe.length !== 18) {
          errors.clabe = t("error_clabe_invalid");
        }
        if (beneficiary.trim().length < 2) {
          errors.beneficiary = t("error_recipient_name_required");
        }
      }
      // Argentina (new backend): the CBU_CVU destination requires a 22-digit
      // CBU/CVU (CbuCvuDestinationDto); the CUIT/CUIL is optional but must be
      // 11 digits when provided. Beneficiary mirrors the BRA/MEX UX rule.
      if (isArgentina) {
        const accountNumber = String(formData.recipientFields?.account_number ?? "").replace(/\D/g, "");
        const cuit = String(formData.recipientFields?.cuit ?? "").replace(/\D/g, "");
        const beneficiary = String(formData.recipientFields?.beneficiary ?? "");
        if (accountNumber.length !== 22) {
          errors.account_number = t("error_cbu_cvu_invalid");
        }
        if (cuit.length > 0 && cuit.length !== 11) {
          errors.cuit = t("error_cuit_invalid");
        }
        if (beneficiary.trim().length < 2) {
          errors.beneficiary = t("error_recipient_name_required");
        }
      }
      // Colombia: two rails that ask for the recipient in opposite ways. The rules are a pure function so
      // they can be tested — see `colombiaDestination.ts` for why that matters here specifically.
      if (isColombia) {
        Object.assign(
          errors,
          validateColombiaDestination(formData.recipientFields, brebDirectoryOutcome(brebResolution), {
            recipientNameRequired: t("error_recipient_name_required"),
            tfAccountNumberInvalid: t("error_tf_account_number_invalid"),
            tfBankRequired: t("error_tf_bank_required"),
            tfDocNumberInvalid: t("error_tf_doc_number_invalid"),
            brebKeyInvalid: t("error_breb_key_invalid"),
            brebNotFound: t("breb_lookup_not_found"),
            brebInactive: t("breb_lookup_inactive"),
            brebUnavailable: t("breb_lookup_unavailable"),
          }),
        );
      }
      // The beneficiary is known here, so the exact per-transaction bound of their kind applies to what they
      // receive — the amount the runtime checks at quote and create. `amount_limit` is a non-field key: it
      // invalidates the step and the page renders it above the form with a way back to the amount.
      if (runtimeCorridor) {
        const rail = railForQuote(runtimeCorridor, formData.recipientFields);
        const currency = CURRENCY_BY_RUNTIME_CORRIDOR[runtimeCorridor];
        const violation = resolvePayoutLimitViolation({
          limits: corridors.limitsFor(rail, runtimeCorridor, currency, "PAYOUT"),
          payerType: payoutPayerTypeFor(runtimeCorridor, rail, formData.recipientFields),
          deliveredAmount:
            quoteData?.amounts.payout_net ??
            quoteData?.amounts.receive ??
            parseAmountInputDecimal(formData.amount_destination),
        });
        if (violation) {
          errors.amount_limit = t("step2_limit_blocked", {
            limit: formatLimitAmount(violation.bound, currency, activeLanguage),
          });
        }
      }
      return errors;
    }

    if (!validationSchema) return {};

    try {
      // Step 1 is the only step with a schema (steps 2 and 3 have already returned above), so there is no
      // other branch to pick. The ternary that used to be here suggested a step-2 path that never ran.
      validationSchema.validateSync(formData, { abortEarly: false });
    } catch (err) {
      if (err instanceof ValidationError) {
        err.inner.forEach((error) => {
          if (error.path) {
            errors[error.path] = error.message;
          }
        });
      }
    }

    // Step 1 funds check, against the ledger wallet of the ORIGIN currency — the one the payout debits. No
    // corridor gate: any activated wallet can fund a dispersion, and the runtime converts when the currencies
    // differ. There is no PHP balance in the new backend; the per-transaction bounds are the corridor limits
    // checked below on the delivered amount. Members are pinned to their org server-side, so we gate on user
    // type; INTERNAL staff have no organization of their own, hence no local check (the backend reserve
    // decides).
    if (currentStep === 1 && formData.origin_currency) {
      const currency = formData.origin_currency;
      const usesRuntime = !isInternalUser && !!user;
      const runtimeWallet = usesRuntime ? getRuntimeWalletForCurrency(currency) : undefined;
      const runtimeError = usesRuntime ? getRuntimeWalletError(currency) : null;

      if (usesRuntime) {
        if (runtimeWallet === undefined) {
          // Not fetched, in flight, or failed. On error we BLOCK (never fall back to a PHP number); while it
          // is still loading the amount-required rule already keeps the button disabled.
          // `runtime_balance` is a non-field key: it invalidates the step without rendering under a field.
          if (runtimeError) {
            errors.runtime_balance = t("runtime_balance_error", { currency });
          }
        } else if (runtimeWallet === null) {
          // Fetched, and the org has no wallet in this currency. Distinct from insufficient funds, which
          // reads as "you're short" rather than "you don't have that wallet" — reachable through the
          // `?origin_currency=` deep link even though the selector cannot offer it.
          errors.amount_origin = t("runtime_wallet_missing", { currency });
        } else if (runtimeWallet.status === "frozen") {
          errors.amount_origin = t("runtime_wallet_frozen_warning", { currency });
        } else if (formData.amount_origin && !errors.amount_origin) {
          // The quote's `send` is the exact debit — it already includes the conversion fee, and on a
          // fixed-receive quote the typed origin amount is only the echo the response filled in. Fall back to
          // the typed value so the user still gets feedback during the debounce.
          const requested = quoteData?.amounts.send ?? parseAmountInputDecimal(formData.amount_origin);
          const available = runtimeWallet.balance_available.trim();
          // Exact decimal comparison (BigInt-scaled): ledger amounts are decimal strings with up to 8 dp,
          // and a parseFloat round-trip could flip the sufficiency check at the precision boundary.
          // Malformed input skips this UX check — the backend reserve still rejects at create.
          if (
            requested !== null &&
            isDecimalString(requested) &&
            isDecimalString(available) &&
            compareDecimalStrings(requested, available) > 0
          ) {
            const formattedMax = formatDecimalAmount2(
              available.startsWith("-") ? "0" : available,
              activeLanguage,
            );
            errors.amount_origin = t("runtime_insufficient_funds", {
              currency,
              max: `${getCurrencySymbol(currency)}${formattedMax} ${currency}`,
            });
          }
        }
      }
    }

    // Step 1 corridor limits, on what the beneficiary receives. The beneficiary is not known yet, so this is
    // the envelope of both payer kinds — the exact kind is checked on step 2 and by the backend.
    if (currentStep === 1 && !errors.amount_destination) {
      const corridor = resolveRuntimeCorridor(formData.destination);
      if (corridor) {
        const rail = railForQuote(corridor, formData.recipientFields);
        const currency = CURRENCY_BY_RUNTIME_CORRIDOR[corridor];
        const violation = resolvePayoutLimitViolation({
          limits: corridors.limitsFor(rail, corridor, currency, "PAYOUT"),
          payerType: null,
          deliveredAmount:
            quoteData?.amounts.payout_net ??
            quoteData?.amounts.receive ??
            parseAmountInputDecimal(formData.amount_destination),
        });
        if (violation) {
          errors.amount_destination = t(
            violation.kind === "above_maximum" ? "error_amount_above_max" : "error_amount_below_min",
            { limit: formatLimitAmount(violation.bound, currency, activeLanguage) },
          );
        }
      }
    }

    return errors;
  }, [
    formData,
    validationSchema,
    currentStep,
    getRuntimeWalletForCurrency,
    getRuntimeWalletError,
    verifiedClientIds,
    clientKycLoading,
    clientKycError,
    brebResolution,
    quoteData,
    user,
    isInternalUser,
    corridors,
    activeLanguage,
    t,
  ]);

  /**
   * Kept OUT of `formErrors` on purpose: those are keyed by field name and rendered under the input they
   * belong to, and step 3 renders no fields — `Step3Confirmation` never receives `errors`. Putting it there
   * would disable the button with nothing on screen explaining why.
   *
   * `Date.now()` alongside the flag is belt and braces, not redundancy: the flag is what makes this memo
   * re-evaluate when the countdown fires, while the clock catches the case where `setTimeout` was throttled
   * in a background tab and the countdown never fired at all.
   */
  const quoteBlock = useMemo<PayoutQuoteBlock>(() => {
    if (currentStep !== 3) return null;

    const corridor = resolveRuntimeCorridor(formData.destination);
    return resolvePayoutQuoteBlock({
      quote: quoteData,
      isLoading: quoteLoading,
      isExpiredFlag: quoteExpired,
      now: Date.now(),
      corridor,
      // From `railForQuote`, never the corridor default: Colombia defaults to BREB and would block every
      // Transfiya payout.
      rail: corridor ? railForQuote(corridor, formData.recipientFields) : null,
    });
  }, [currentStep, quoteData, quoteLoading, quoteExpired, formData.destination, formData.recipientFields]);

  /**
   * Why step 3 cannot be authorized because of WHO RECEIVES, as opposed to the price.
   *
   * Separate from `quoteBlock` because the way out is different — this one is a trip back to step 2, not a
   * new price — and kept out of `formErrors` for the reason stated above it: step 3 renders no fields, so an
   * error keyed by a field name would disable the button with nothing on screen explaining why.
   *
   * The rule itself is `resolveBrebAuthorizationBlock`, a pure function next to the step-2 rules it has to
   * stay coherent with. Only the context is decided here: which step, and which corridor.
   */
  const recipientBlock = useMemo<string | null>(() => {
    if (currentStep !== 3) return null;
    if (resolveRuntimeCorridor(formData.destination) !== "COL") return null;
    return resolveBrebAuthorizationBlock(formData.recipientFields, brebDirectoryOutcome(brebResolution), {
      brebNotFound: t("breb_lookup_not_found"),
      brebInactive: t("breb_lookup_inactive"),
      brebUnavailable: t("breb_lookup_unavailable"),
      brebChecking: t("breb_lookup_loading"),
    });
  }, [currentStep, formData.destination, formData.recipientFields, brebResolution, t]);

  const isFormValid = Object.keys(formErrors).length === 0 && quoteBlock === null && recipientBlock === null;

  return {
    formErrors,
    isFormValid,
    quoteBlockReason: quoteBlock ? t(QUOTE_BLOCK_MESSAGE[quoteBlock]) : null,
    recipientBlockReason: recipientBlock,
  };
}
